import React, { useState, useEffect, useRef } from 'react';
import { isMultilingual, getAvailableLanguages, setLanguage, useLanguage } from '../utils/i18n';
import { DEFAULT_LANGUAGE, LANGUAGE_REGISTRY } from '../utils/i18nRegistry';
import { TamilKeypadInput, hasOnScreenKeypad } from '../components/TamilKeypad';
import { readDeviceLanguage, writeDeviceLanguage } from '../utils/languageStorage';
import { localizedName } from '../utils/menuName';
import { createPortal } from 'react-dom';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';
import AdminNavTabs from '../components/AdminNavTabs';
import './AdminCategories.css';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';
const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';

const AdminCategories = () => {
  const navigate = useNavigate();
  // Re-render when the tenant's language config arrives (loaded by AdminNavTabs off
  // the /admin/general call it already makes). Without this the translation inputs
  // would stay hidden until some unrelated state change re-rendered the page.
  //
  // useLanguage() also RETURNS the active display language, which is what the table
  // rows are now localized into. Same per-device singleton the Take Order screen
  // uses, so a language chosen here and there stay in step.
  const activeLanguage = useLanguage();
  // Scope key for the per-device language selection — identical to AdminTakeOrder so
  // a selection is shared across the admin surfaces of one deployment (and can never
  // leak between tenants on a shared origin).
  const LANGUAGE_TENANT_KEY = PROJECT_NAME;

  const onChangeLanguage = (code) => {
    const applied = setLanguage(code);
    writeDeviceLanguage(LANGUAGE_TENANT_KEY, applied);
  };
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [username, setUsername] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all, root, subcategories
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingAll, setDeletingAll] = useState(false);
  const statusRef = useRef(null);

  const [formData, setFormData] = useState({
    ProductName: '',
    ProductNameI18n: {},
    ParentCategoryID: '',
    Description: '',
    ImageURL: '',
  });

  // Lock background scroll while the modal is open
  useEffect(() => {
    if (showForm) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [showForm]);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(`${API_BASE_URL}/admin/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Categories');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }
        const user = await Auth.currentAuthenticatedUser();
        setUsername(user.username || '');
        await fetchCategories();
      } catch (err) {
        console.error('Auth error:', err);
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Restore this device's saved language once the tenant config has arrived and the
  // tenant is multilingual. Runs on the same useLanguage() tick that flips
  // isMultilingual() true, so a device that last chose Tamil on the Take Order screen
  // opens Categories in Tamil too. A stored code the tenant has since removed is
  // clamped to the default by setLanguage.
  useEffect(() => {
    if (!isMultilingual()) return;
    const stored = readDeviceLanguage(LANGUAGE_TENANT_KEY);
    if (stored) setLanguage(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLanguage]);

  // Scroll to status message
  useEffect(() => {
    if ((error || successMessage) && statusRef.current) {
      statusRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error, successMessage]);

  // Get root categories (for parent dropdown)
  const rootCategories = categories.filter(cat => !cat.ParentCategoryID);

  // Build category hierarchy map
  const categoryMap = {};
  const subcategoryMap = {};
  categories.forEach(cat => {
    categoryMap[cat.ProductID] = cat;
    if (cat.ParentCategoryID) {
      if (!subcategoryMap[cat.ParentCategoryID]) {
        subcategoryMap[cat.ParentCategoryID] = [];
      }
      subcategoryMap[cat.ParentCategoryID].push(cat);
    }
  });

  // Filter and search categories
  const filteredCategories = categories.filter(cat => {
    // Filter by mode
    if (filterMode === 'root' && cat.ParentCategoryID) return false;
    if (filterMode === 'subcategories' && !cat.ParentCategoryID) return false;

    // Search by name
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const name = (cat.ProductName || '').toLowerCase();
      const parentName = cat.ParentCategoryID 
        ? (categoryMap[cat.ParentCategoryID]?.ProductName || '').toLowerCase()
        : '';
      return name.includes(query) || parentName.includes(query);
    }

    return true;
  });

  // Sort categories: root categories first, then subcategories under their parents
  const sortedCategories = [...filteredCategories].sort((a, b) => {
    // If both are root or both are subcategories, sort alphabetically
    if ((!a.ParentCategoryID && !b.ParentCategoryID) || 
        (a.ParentCategoryID && b.ParentCategoryID)) {
      return (a.ProductName || '').localeCompare(b.ProductName || '');
    }
    // Root categories come before subcategories
    return a.ParentCategoryID ? 1 : -1;
  });

  const handleCreateNew = () => {
    setEditingCategory(null);
    setFormData({
      ProductName: '',
      ProductNameI18n: {},
      ParentCategoryID: '',
      Description: '',
      ImageURL: '',
    });
    setShowForm(true);
    setError('');
    setSuccessMessage('');
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      ProductName: category.ProductName || '',
      // Translated names for the editor inputs (take-order-i18n R8.3).
      ProductNameI18n:
        category.ProductNameI18n && typeof category.ProductNameI18n === 'object'
          ? category.ProductNameI18n
          : {},
      ParentCategoryID: category.ParentCategoryID || '',
      Description: category.Description || '',
      ImageURL: category.ImageURL || '',
    });
    setShowForm(true);
    setError('');
    setSuccessMessage('');
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData({
      ProductName: '',
      ProductNameI18n: {},
      ParentCategoryID: '',
      Description: '',
      ImageURL: '',
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // Prepare payload
      const payload = {
        ProductName: formData.ProductName.trim(),
        // Sent only for a multilingual tenant, so a single-language tenant's
        // request body is unchanged (R8.2). Backend discards unknown codes and
        // drops blanks, which is what makes clearing an input restore the
        // English fallback (R7.5 / R8.4).
        ...(isMultilingual() ? { ProductNameI18n: formData.ProductNameI18n || {} } : {}),
        Description: formData.Description.trim(),
        ImageURL: formData.ImageURL.trim(),
      };

      // Handle parent category (empty string means no parent)
      if (formData.ParentCategoryID && formData.ParentCategoryID.trim()) {
        payload.ParentCategoryID = formData.ParentCategoryID.trim();
      } else {
        payload.ParentCategoryID = null;
      }

      const url = editingCategory
        ? `${API_BASE_URL}/admin/categories/${editingCategory.ProductID}`
        : `${API_BASE_URL}/admin/categories`;

      const method = editingCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(friendlyApiError(response.status, data, 'Failed to save category'));
      }

      setSuccessMessage(
        editingCategory
          ? 'Category updated successfully'
          : 'Category created successfully'
      );
      setShowForm(false);
      setEditingCategory(null);
      setFormData({
        ProductName: '',
      ProductNameI18n: {},
        ParentCategoryID: '',
        Description: '',
        ImageURL: '',
      });

      // Refresh categories
      await fetchCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.message || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (category) => {
    const subcategories = subcategoryMap[category.ProductID] || [];
    const confirmMessage = subcategories.length > 0
      ? `Delete "${category.ProductName}"? Its ${subcategories.length} subcategory(ies) will be promoted to root categories.`
      : `Delete "${category.ProductName}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(
        `${API_BASE_URL}/admin/categories/${category.ProductID}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(friendlyApiError(response.status, data, 'Failed to delete category'));
      }

      setSuccessMessage('Category deleted successfully');
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.message || 'Failed to delete category');
    }
  };

  // Delete ALL categories (destructive). Mirrors the single-delete endpoint,
  // iterating over every loaded category. Products keep their Category name and
  // are simply no longer backed by a category record (same behavior as deleting
  // categories one by one). Requires an explicit confirmation.
  const handleDeleteAll = async () => {
    const total = categories.length;
    if (total === 0 || deletingAll) return;

    const confirmMessage =
      `Delete ALL ${total} categor${total === 1 ? 'y' : 'ies'}? This cannot be undone. ` +
      `Products are not deleted, but they will no longer belong to any category.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingAll(true);
    setError('');
    setSuccessMessage('');
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // Snapshot the ids up front so promotion/refresh can't shift the list.
      const ids = categories
        .map((c) => c && c.ProductID)
        .filter(Boolean);

      let okCount = 0;
      let failCount = 0;
      for (const id of ids) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(`${API_BASE_URL}/admin/categories/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!response.ok) {
            failCount += 1;
          } else {
            okCount += 1;
          }
        } catch (e) {
          failCount += 1;
        }
      }

      if (failCount === 0) {
        setSuccessMessage(`Deleted all ${okCount} categories.`);
      } else {
        setError(`Deleted ${okCount} of ${ids.length} categories; ${failCount} failed. You can retry.`);
      }
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting all categories:', err);
      setError(err.message || 'Failed to delete all categories');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-sitemap"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Category Management</h1>
            <p>Organize your product catalog</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-categories-container">
        <div className="admin-categories-header">
          <button
            className="btn-create-category"
            onClick={handleCreateNew}
            disabled={showForm || deletingAll}
          >
            <i className="fas fa-plus"></i> Create Category
          </button>
          <button
            className="btn-delete-all-categories"
            onClick={handleDeleteAll}
            disabled={showForm || deletingAll || categories.length === 0}
            title="Delete all categories"
          >
            <i className={`fas ${deletingAll ? 'fa-spinner fa-spin' : 'fa-trash'}`}></i>{' '}
            {deletingAll ? 'Deleting…' : 'Delete All'}
          </button>

          {/* Language switcher — only for a multilingual tenant, so a single-language
              tenant's toolbar is unchanged. Picks the language the category names in the
              table below are shown in, and shares the choice with the Take Order screen. */}
          {isMultilingual() ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>Language:</span>
              {getAvailableLanguages().map((code) => {
                const active = activeLanguage === code;
                const label = LANGUAGE_REGISTRY[code] ? LANGUAGE_REGISTRY[code].label : code;
                return (
                  <button
                    key={`lang-${code}`}
                    type="button"
                    onClick={() => onChangeLanguage(code)}
                    aria-pressed={active}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 6,
                      border: `1px solid ${active ? '#7c3aed' : '#cbd5e1'}`,
                      background: active ? '#7c3aed' : '#fff',
                      color: active ? '#fff' : '#334155',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Status Messages */}
        <div ref={statusRef}>
          {error && (
            <div className="alert alert-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}
          {successMessage && (
            <div className="alert alert-success">
              <i className="fas fa-check-circle"></i> {successMessage}
            </div>
          )}
        </div>

        {/* Category Form */}
        {showForm && createPortal(
          <div className="category-form-overlay" onMouseDown={handleCancelForm}>
            <div className="category-form-card" onMouseDown={(e) => e.stopPropagation()}>
              <h2>{editingCategory ? 'Edit Category' : 'Create New Category'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="categoryName">
                    Category Name <span className="required">*</span>
                  </label>
                  <input
                    id="categoryName"
                    type="text"
                    value={formData.ProductName}
                    onChange={(e) =>
                      setFormData({ ...formData, ProductName: e.target.value })
                    }
                    required
                    maxLength={255}
                    placeholder="Enter category name"
                    disabled={submitting}
                  />
                </div>

                {/* Translated category names (take-order-i18n R8.1 / R8.2).
                    Rendered ONLY for a multilingual tenant, so a single-language
                    tenant's form is byte-identical to pre-feature. */}
                {isMultilingual()
                  ? getAvailableLanguages()
                      .filter((code) => code !== DEFAULT_LANGUAGE)
                      .map((code) => {
                        const label = LANGUAGE_REGISTRY[code]
                          ? LANGUAGE_REGISTRY[code].label
                          : code;
                        const current = String(
                          (formData.ProductNameI18n && formData.ProductNameI18n[code]) || ''
                        );
                        return (
                          <div className="form-group" key={`cat-i18n-${code}`}>
                            <label htmlFor={`categoryName-${code}`}>
                              Category Name ({label})
                            </label>
                            {/* On-screen palette so an admin with no Tamil input
                                method configured can still enter the name. */}
                            <TamilKeypadInput
                              id={`categoryName-${code}`}
                              value={current}
                              onValueChange={(next) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  ProductNameI18n: {
                                    ...(prev.ProductNameI18n || {}),
                                    [code]: next,
                                  },
                                }))
                              }
                              showKeypad={hasOnScreenKeypad(code)}
                              keypadLabel={`Category Name (${label})`}
                              maxLength={255}
                              // R8.5 — show the English name as the fallback, so an
                              // admin can see what staff will read if this is left blank.
                              placeholder={
                                String(formData.ProductName || '').trim() ||
                                `Enter category name in ${label}`
                              }
                              disabled={submitting}
                            />
                            <small style={{ color: '#64748b' }}>
                              Leave blank to show the English name.
                            </small>
                          </div>
                        );
                      })
                  : null}

                <div className="form-group">
                  <label htmlFor="parentCategory">Parent Category</label>
                  <select
                    id="parentCategory"
                    value={formData.ParentCategoryID}
                    onChange={(e) =>
                      setFormData({ ...formData, ParentCategoryID: e.target.value })
                    }
                    disabled={submitting}
                  >
                    <option value="">None (Root Category)</option>
                    {rootCategories
                      .filter(cat => !editingCategory || cat.ProductID !== editingCategory.ProductID)
                      .map((cat) => (
                        <option key={cat.ProductID} value={cat.ProductID}>
                          {cat.ProductName}
                        </option>
                      ))}
                  </select>
                  <small className="form-help">
                    Select a parent category to create a subcategory. Leave empty for a root category.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.Description}
                    onChange={(e) =>
                      setFormData({ ...formData, Description: e.target.value })
                    }
                    rows={3}
                    placeholder="Enter category description"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="imageUrl">Image URL</label>
                  <input
                    id="imageUrl"
                    type="url"
                    value={formData.ImageURL}
                    onChange={(e) =>
                      setFormData({ ...formData, ImageURL: e.target.value })
                    }
                    placeholder="https://example.com/image.jpg"
                    disabled={submitting}
                  />
                  {formData.ImageURL && (
                    <div className="image-preview">
                      <img src={formData.ImageURL} alt="Category preview" />
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={handleCancelForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={submitting || !formData.ProductName.trim()}
                  >
                    {submitting ? 'Saving...' : editingCategory ? 'Update Category' : 'Create Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* Filters and Search */}
        <div className="category-filters">
          <div className="filter-buttons">
            <button
              className={filterMode === 'all' ? 'active' : ''}
              onClick={() => setFilterMode('all')}
            >
              All Categories ({categories.length})
            </button>
            <button
              className={filterMode === 'root' ? 'active' : ''}
              onClick={() => setFilterMode('root')}
            >
              Root Only ({rootCategories.length})
            </button>
            <button
              className={filterMode === 'subcategories' ? 'active' : ''}
              onClick={() => setFilterMode('subcategories')}
            >
              Subcategories Only ({categories.filter(c => c.ParentCategoryID).length})
            </button>
          </div>

          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Category List */}
        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i> Loading categories...
          </div>
        ) : sortedCategories.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-folder-open"></i>
            <p>No categories found</p>
            {!showForm && (
              <button className="btn-create-category" onClick={handleCreateNew}>
                Create Your First Category
              </button>
            )}
          </div>
        ) : (
          <div className="category-list">
            <table className="category-table">
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>Parent Category</th>
                  <th>Subcategories</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCategories.map((category) => {
                  const isSubcategory = !!category.ParentCategoryID;
                  const parentCategory = isSubcategory
                    ? categoryMap[category.ParentCategoryID]
                    : null;
                  const subcategoryCount = (subcategoryMap[category.ProductID] || []).length;

                  return (
                    <tr
                      key={category.ProductID}
                      className={isSubcategory ? 'subcategory-row' : 'root-category-row'}
                    >
                      <td className="category-name-cell">
                        {isSubcategory && <span className="indent-marker">└─</span>}
                        {category.ImageURL && (
                          <img
                            src={category.ImageURL}
                            alt={category.ProductName}
                            className="category-thumbnail"
                          />
                        )}
                        {/* Display-only translation. The stored English ProductName is
                            untouched and remains the submitted value and the join key. */}
                        <span className="category-name">{localizedName(category, activeLanguage)}</span>
                      </td>
                      <td className="parent-category-cell">
                        {parentCategory ? (
                          <span className="parent-badge">{localizedName(parentCategory, activeLanguage)}</span>
                        ) : (
                          <span className="root-badge">Root</span>
                        )}
                      </td>
                      <td className="subcategory-count-cell">
                        {subcategoryCount > 0 ? (
                          <span className="subcategory-badge">
                            {subcategoryCount} {subcategoryCount === 1 ? 'subcategory' : 'subcategories'}
                          </span>
                        ) : (
                          <span className="no-subcategories">—</span>
                        )}
                      </td>
                      <td className="description-cell">
                        {category.Description || <span className="no-description">—</span>}
                      </td>
                      <td className="actions-cell">
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(category)}
                          title="Edit category"
                          disabled={showForm}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(category)}
                          title="Delete category"
                          disabled={showForm}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCategories;
